import React from "react";
import { createRoot, Root } from "react-dom/client";
import PublicCommentApp from "./PublicCommentApp";
import type { PublicCommentConfig } from "./types";

declare global {
  interface Window {
    renderPublicComment?: (
      container?: HTMLElement | null,
      config?: PublicCommentConfig | null
    ) => boolean;
    modelData?: {
      publicCommentConfig?: PublicCommentConfig;
      [key: string]: any;
    };
  }
}

// One React root per container element. The portal / calendar events list mounts this
// module on demand when a resident opens a meeting's Comments tab, and the same tab can
// be opened, closed and reopened; calling createRoot twice on one node warns and leaks,
// so reuse the root we already made for that node.
const rootsByContainer = new WeakMap<HTMLElement, Root>();

// React 18 renders off the call stack, so a component that throws does NOT surface as an
// exception from root.render() -- without a boundary the container keeps whatever it was
// rendered with, which on the portal is the "Loading public comment form..." placeholder.
// The boundary replaces it with the same recoverable-error copy CalendarEmbed shows for
// its own failure paths, and role="alert" corrects the "form loaded" announcement.
class PublicCommentErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("Public Comment: React error", error, info.componentStack);
  }
  render() {
    if (this.state.error) {
      return (
        <p className="comments-disabled-note pc-load-error" role="alert">
          Comments could not be loaded. Please refresh the page and try again.
        </p>
      );
    }
    return this.props.children;
  }
}

function getRoot(container: HTMLElement): Root {
  const existing = rootsByContainer.get(container);
  if (existing) return existing;

  const root = createRoot(container);
  rootsByContainer.set(container, root);
  return root;
}

/**
 * Mount the public comment app.
 *
 * Called two ways:
 *  - with no arguments (meeting detail page): resolves the single `#public-comment-root`
 *    element and reads `window.modelData.publicCommentConfig`. This is the original
 *    behavior and is what the DOMContentLoaded bootstrap below still uses.
 *  - with an explicit container and config (portal / calendar events list): a page there
 *    renders many meetings, so neither a fixed element id nor a single global config can
 *    identify which meeting to mount. Mirrors `window.renderPuckContent(container, data)`.
 */
function renderPublicComment(
  container?: HTMLElement | null,
  config?: PublicCommentConfig | null
): boolean {
  try {
    const target = container || document.getElementById("public-comment-root");
    if (!target) return false;

    const resolvedConfig = config || window.modelData?.publicCommentConfig;
    if (!resolvedConfig) {
      console.error(
        "Public Comment: Config not found on window.modelData.publicCommentConfig"
      );
      return false;
    }

    getRoot(target).render(
      <PublicCommentErrorBoundary>
        <PublicCommentApp config={resolvedConfig} />
      </PublicCommentErrorBoundary>
    );
    return true;
  } catch (error) {
    console.error("Public Comment: Error during initialization", error);
    return false;
  }
}

window.renderPublicComment = renderPublicComment;

// Auto-mount for the meeting detail page, where the container is server-rendered and
// present at load. On the portal the container arrives with an AJAX-loaded event list and
// there is nothing to find here, so this is a no-op and CalendarEmbed calls in explicitly.
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => renderPublicComment());
} else {
  renderPublicComment();
}

export default PublicCommentApp;
